// src/pages/bloomterminal/alerts/Alerts.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NavigationButtons from './components/NavigationButtons';
import PageTitle from './components/PageTitle';
import AlertTypeToggle from './components/AlertTypeToggle';
import CommoditySelector from './steps/AlertCommoditySelect';
import SpecificAlertTypeSelector from './steps/SpecificAlertTypeSelector';
import RegionSelector from './steps/RegionSelector';
import AlertTypeSelector from './steps/AlertTypeSelector';
import CustomAlertTypeConfiguration from './steps/CustomAlertTypeConfiguration';
import CustomAlertCommodityConfig from './steps/CustomAlertCommodityConfig';
import { useRegions, useCreateSmartAlert, useCreateCustomAlert } from '../../utils/api-hooks/alerts';
import { useUser } from '../../context/UserContext';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { trackEvent, trackAlertCreated, EVENTS } from '../../utils/analytics';

// Helper function to map operator values from UI format to API format
const mapOperatorToApiFormat = (operator) => {
  const operatorMap = {
    'greaterThan': 'greater_than',
    'lessThan': 'less_than',
    'equalTo': 'equals_to',
    'inBetween': 'in_between',
  };
  return operatorMap[operator] || operator;
};

// Helper function to get AlertType from SpecificAlertType
const getAlertTypeFromSpecificAlertType = (specificAlertType) => {
  switch (specificAlertType) {
    case 'fob_price_alert':
    case 'fob_price':
    case 'both':
    case 'price_change':
    case 'price_changes':
      return 'price';
    case 'price_increase':
      return 'price';
    case 'price_decrease':
      return 'price';
    case 'volume':
      return 'volume';
    case 'rain':
      return 'rain';
    case 'temperature':
      return 'temperature';
    case 'weather':
      return 'weather';
    case 'market':
      return 'market';
    default:
      return 'price';
  }
};

const Alerts = ({
  initialAlertType,
  initialCurrentStep,
  initialSelectedCustomAlertType,
  initialCustomAlertConfiguration,
  initialSkuSelection,
  initialSelectedCommodity,
  initialSelectedSpecificAlertType,
  initialSelectedRegions,
  submitHandlers,
  pageTitle,
  onExit,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const [alertType, setAlertType] = useState(initialAlertType ?? 'smart'); // 'smart' or 'custom'
  const [currentStep, setCurrentStep] = useState(initialCurrentStep ?? 1);

  // Custom alert type flow state
  const [selectedCustomAlertType, setSelectedCustomAlertType] = useState(
    initialSelectedCustomAlertType ?? null
  ); // 'price' | 'volume' | 'rain' | 'temperature' | null
  const [customAlertConfiguration, setCustomAlertConfiguration] = useState(
    initialCustomAlertConfiguration ?? {
      operator: null,
      granularity: null,
      value: '',
      minValue: '',
      maxValue: '',
    }
  );

  // SKU selection state for custom Price/Volume alerts
  const [skuSelection, setSkuSelection] = useState({
    // Price custom alerts (match FOB Price Pulse cascade)
    district: '',
    type: '',
    variety: '',
    size: '',
    grade: '',
    package: '',
    storage: '',
    condition: '',
    environment: '',
    quality: '',
    appearance: '',
    properties: '',

    // Volume custom alerts (match Volume by Origin cascade)
    types: [],
    varieties: [],
    origins: [],
  });

  // Step 1 state - Commodity selection (data now fetched via useCommodities hook in CommoditySelector)
  const [selectedCommodity, setSelectedCommodity] = useState(initialSelectedCommodity ?? null); // string | null

  // Step 2 state - Only allow one alert type selection
  const [selectedSpecificAlertType, setSelectedSpecificAlertType] = useState(
    initialSelectedSpecificAlertType ?? null
  ); // string | null

  // Step 3 state - Object with parent keys and arrays of selected child regions
  const [selectedRegions, setSelectedRegions] = useState(initialSelectedRegions ?? {});

  // Success/Error states
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Get user context for email and tier
  const { email, tier } = useUser();

  // Get the alert type for fetching regions
  const derivedAlertType = getAlertTypeFromSpecificAlertType(selectedSpecificAlertType);
  const regionsAlertType = alertType === 'smart' ? derivedAlertType : selectedCustomAlertType;
  const regionsDefaultOrCustom = alertType === 'smart' ? 'default' : 'custom';

  // Fetch regions based on selected commodity and alert type
  const { regions, isLoading: isLoadingRegions } = useRegions(
    regionsAlertType,
    selectedCommodity,
    regionsDefaultOrCustom
  );

  // Reconcile prefilled selections with loaded region keys/values (case/spacing differences).
  // This is important for edit mode, where saved selections may differ in casing from API region lists.
  const regionsKeyMap = useMemo(() => {
    const map = new Map();
    Object.keys(regions || {}).forEach((k) => {
      map.set(String(k).trim().toLowerCase(), k);
    });
    return map;
  }, [regions]);

  useEffect(() => {
    if (!regions || Object.keys(regions).length === 0) return;
    if (!selectedRegions || Object.keys(selectedRegions).length === 0) return;

    setSelectedRegions((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;

      const next = {};
      let changed = false;

      Object.entries(prev).forEach(([parentKey, children]) => {
        const normalizedParentLookup = String(parentKey).trim().toLowerCase();
        const canonicalParentKey = regionsKeyMap.get(normalizedParentLookup) || parentKey;
        if (canonicalParentKey !== parentKey) changed = true;

        const availableChildren = Array.isArray(regions?.[canonicalParentKey])
          ? regions[canonicalParentKey].filter((x) => x != null).map((x) => String(x))
          : [];
        const availableChildMap = new Map(
          availableChildren.map((c) => [c.trim().toLowerCase(), c])
        );

        const selectedList = Array.isArray(children) ? children : children != null ? [children] : [];
        const mappedChildren = selectedList
          .filter((x) => x != null)
          .map((x) => String(x))
          .map((c) => availableChildMap.get(c.trim().toLowerCase()) || c);

        // If we mapped something, mark changed
        if (mappedChildren.some((c, idx) => c !== String(selectedList[idx] ?? ''))) {
          changed = true;
        }

        next[canonicalParentKey] = mappedChildren;
      });

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionsKeyMap]);

  // Smart alert creation mutation
  const createSmartAlertMutation = useCreateSmartAlert();

  // Custom alert creation mutation
  const createCustomAlertMutation = useCreateCustomAlert();

  // Step 1 handlers - Only allow one commodity selection
  const handleCommodityToggle = (commodity) => {
    // If clicking the same commodity, deselect it
    if (selectedCommodity === commodity) {
      setSelectedCommodity(null);
    } else {
      // Otherwise, set the new selection
      setSelectedCommodity(commodity);
    }
  };

  // Step 2 handlers - Only allow one alert type selection (for smart flow)
  const handleSpecificAlertTypeToggle = (alertTypeId) => {
    // If clicking the same alert type, deselect it
    if (selectedSpecificAlertType === alertTypeId) {
      setSelectedSpecificAlertType(null);
    } else {
      // Otherwise, set the new selection
      setSelectedSpecificAlertType(alertTypeId);
    }
  };

  // Custom alert type handlers
  const handleCustomAlertTypeToggle = (alertTypeId) => {
    // If clicking the same alert type, deselect it
    if (selectedCustomAlertType === alertTypeId) {
      setSelectedCustomAlertType(null);
    } else {
      // Otherwise, set the new selection and reset downstream state
      setSelectedCustomAlertType(alertTypeId);
      // Reset commodity and SKU when alert type changes
      setSelectedCommodity(null);
      setSkuSelection({
        district: '',
        type: '',
        variety: '',
        size: '',
        grade: '',
        package: '',
        storage: '',
        condition: '',
        environment: '',
        quality: '',
        appearance: '',
        properties: '',
        types: [],
        varieties: [],
        origins: [],
      });
    }
  };

  // SKU change handler
  const handleSkuChange = (newSkuSelection) => {
    setSkuSelection(newSkuSelection);
  };

  const handleCustomConfigurationChange = (config) => {
    setCustomAlertConfiguration(config);
  };

  // Step 3 handlers
  const handleRegionToggle = (parentKey, childRegion) => {
    setSelectedRegions(prev => {
      const parentRegions = prev[parentKey] || [];
      const isSelected = parentRegions.includes(childRegion);
      
      return {
        ...prev,
        [parentKey]: isSelected
          ? parentRegions.filter(r => r !== childRegion)
          : [...parentRegions, childRegion],
      };
    });
  };

  const handleSelectAll = (parentKey, children) => {
    setSelectedRegions(prev => ({
      ...prev,
      [parentKey]: [...children],
    }));
  };

  const handleClearAll = (parentKey) => {
    setSelectedRegions(prev => ({
      ...prev,
      [parentKey]: [],
    }));
  };

  // Navigation handlers
  const getMaxSteps = () => {
    if (alertType === 'smart') {
      return derivedAlertType === 'market' ? 2 : 3;
    }
    return 4;
  };

  const handleNext = async () => {
    const maxSteps = getMaxSteps();
    if (currentStep < maxSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Submit form
      if (alertType === 'smart') {
        // Clear any previous messages
        setErrorMessage('');
        setSuccessMessage('');

        try {
          const payload = {
            user_email: email,
            commodity: selectedCommodity,
            alert_type: derivedAlertType,
            specific_alert_type: selectedSpecificAlertType,
            regions: derivedAlertType === 'market' ? null : selectedRegions,
            enabled: true,
            user_tier: tier,
          };

          console.log('CREATE_SMART_ALERT PAYLOAD', payload);

          if (submitHandlers?.smart) {
            await submitHandlers.smart(payload);
          } else {
            await createSmartAlertMutation.mutateAsync(payload);
          }

          // Track successful alert creation
          trackAlertCreated('smart', selectedCommodity, selectedSpecificAlertType);

          // Show success message
          setSuccessMessage(t('alerts.smartAlertSuccess'));
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);

          // Reset the form
          setCurrentStep(1);
          setSelectedCommodity(null);
          setSelectedSpecificAlertType(null);
          setSelectedRegions({});
        } catch (error) {
          console.error('Error creating smart alert:', error);
          trackEvent(EVENTS.ALERT_CREATION_ERROR, { alert_type: 'smart', error: error.message });
          setErrorMessage(error.message || t('alerts.errorCreatingAlert'));
          setTimeout(() => {
            setErrorMessage('');
          }, 5000);
        }
      } else {
        // Custom alert submission
        // Clear any previous messages
        setErrorMessage('');
        setSuccessMessage('');

        try {
          const payload = {
            user_email: email,
            commodity: selectedCommodity,
            alert_type: selectedCustomAlertType,
            operator: mapOperatorToApiFormat(customAlertConfiguration.operator),
            regions: selectedRegions,
            enabled: true,
            granularity: [customAlertConfiguration.granularity],
          };

          // Add value fields based on operator type
          if (customAlertConfiguration.operator === 'inBetween') {
            payload.min_value = customAlertConfiguration.minValue;
            payload.max_value = customAlertConfiguration.maxValue;
          } else {
            payload.value = customAlertConfiguration.value;
          }

          // Add SKU data for Price alerts
          if (selectedCustomAlertType === 'price') {
            payload.skus = {
              district: skuSelection.district,
              variety: skuSelection.variety,
              size: skuSelection.size,
              grade: skuSelection.grade,
              type: skuSelection.type,
              sku: skuSelection.package,
              storage: skuSelection.storage,
              condition: skuSelection.condition,
              environment: skuSelection.environment,
              quality: skuSelection.quality,
              appearance: skuSelection.appearance,
              properties: skuSelection.properties,
            };
          }

          // Add SKU data for Volume alerts
          if (selectedCustomAlertType === 'volume') {
            payload.skus = {
              types: skuSelection.types,
              varieties: skuSelection.varieties,
              origins: skuSelection.origins,
            };
          }

          console.log('CREATE_CUSTOM_ALERT PAYLOAD', payload);

          if (submitHandlers?.custom) {
            await submitHandlers.custom(payload);
          } else {
            await createCustomAlertMutation.mutateAsync(payload);
          }

          // Track successful custom alert creation
          trackAlertCreated('custom', selectedCommodity, selectedCustomAlertType);

          // Show success message
          setSuccessMessage(t('alerts.customAlertSuccess'));
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);

          // Reset the form
          setCurrentStep(1);
          setSelectedCustomAlertType(null);
          setSelectedCommodity(null);
          setSelectedRegions({});
          setCustomAlertConfiguration({
            operator: null,
            granularity: null,
            value: '',
            minValue: '',
            maxValue: '',
          });
          setSkuSelection({
            district: '',
            type: '',
            variety: '',
            size: '',
            grade: '',
            package: '',
            storage: '',
            condition: '',
            environment: '',
            quality: '',
            appearance: '',
            properties: '',
            types: [],
            varieties: [],
            origins: [],
          });
        } catch (error) {
          console.error('Error creating custom alert:', error);
          trackEvent(EVENTS.ALERT_CREATION_ERROR, { alert_type: 'custom', error: error.message });
          setErrorMessage(error.message || t('alerts.errorCreatingCustomAlert'));
          setTimeout(() => {
            setErrorMessage('');
          }, 5000);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      return;
    }

    // Step 1 "Back" acts as exit when provided (e.g. edit flow).
    onExit?.();
  };

  const handleAlertTypeChange = (newAlertType) => {
    trackEvent(EVENTS.ALERT_TYPE_CHANGED, { alert_type: newAlertType });
    setAlertType(newAlertType);
    setCurrentStep(1); // Reset to step 1 when switching between smart/custom
  };

  // Helper to check if custom alert type is weather-related (no SKU needed)
  const isWeatherRelatedAlertType = (type) => ['rain', 'temperature'].includes(type);

  // Step validation
  const canProceedStep1 = alertType === 'smart' 
    ? selectedCommodity !== null 
    : selectedCustomAlertType !== null;
  
  // Custom alert step 2 validation includes SKU for Price/Volume
  const getCanProceedCustomStep2 = () => {
    if (!selectedCommodity) return false;
    
    // Weather alerts only need commodity
    if (isWeatherRelatedAlertType(selectedCustomAlertType)) return true;
    
    // Price alerts need all SKU fields
    if (selectedCustomAlertType === 'price') {
      return (
        skuSelection.type &&
        skuSelection.variety &&
        skuSelection.size &&
        skuSelection.grade &&
        skuSelection.package
      );
    }
    
    // Volume alerts need types + varieties + origins (all multi-select)
    if (selectedCustomAlertType === 'volume') {
      return (
        Array.isArray(skuSelection.types) && skuSelection.types.length > 0 &&
        Array.isArray(skuSelection.varieties) && skuSelection.varieties.length > 0
      );
    }
    
    return true;
  };

  const canProceedStep2 = alertType === 'smart'
    ? selectedSpecificAlertType !== null
    : getCanProceedCustomStep2();
  const canProceedStep3 = Object.values(selectedRegions).some(regions => regions.length > 0);
  
  // Step 4 validation - handle in_between operator differently
  const getCanProceedStep4 = () => {
    if (alertType !== 'custom') return false;
    if (!customAlertConfiguration.operator || !customAlertConfiguration.granularity) return false;
    
    // For in_between operator, check min and max values
    if (customAlertConfiguration.operator === 'inBetween') {
      if (!customAlertConfiguration.minValue || !customAlertConfiguration.maxValue) return false;
      // Validate that min < max
      const min = parseFloat(customAlertConfiguration.minValue);
      const max = parseFloat(customAlertConfiguration.maxValue);
      if (isNaN(min) || isNaN(max) || min >= max) return false;
      return true;
    }
    
    // For other operators, check single value
    return customAlertConfiguration.value !== '';
  };
  
  const canProceedStep4 = getCanProceedStep4();

  const getStepTitle = () => {
    if (alertType === 'smart') {
      switch (currentStep) {
        case 1:
          return t('alerts.step1Commodities');
        case 2:
          return t('alerts.step2AlertType');
        case 3:
          return t('alerts.step3Region');
        default:
          return '';
      }
    } else {
      switch (currentStep) {
        case 1:
          return t('alerts.step1CustomAlert');
        case 2:
          return t('alerts.step2CustomCommodities');
        case 3:
          return t('alerts.step3CustomRegion');
        case 4:
          return t('alerts.step4CustomValues');
        default:
          return '';
      }
    }
  };

  const canProceed = () => {
    if (alertType === 'smart') {
      switch (currentStep) {
        case 1:
          return canProceedStep1;
        case 2:
          return canProceedStep2;
        case 3:
          return canProceedStep3;
        default:
          return false;
      }
    } else {
      switch (currentStep) {
        case 1:
          return canProceedStep1;
        case 2:
          return canProceedStep2;
        case 3:
          return canProceedStep3;
        case 4:
          return canProceedStep4;
        default:
          return false;
      }
    }
  };

  const renderStepContent = () => {
    if (alertType === 'smart') {
      switch (currentStep) {
        case 1:
          return (
            <CommoditySelector
              selectedCommodity={selectedCommodity}
              onCommodityToggle={handleCommodityToggle}
            />
          );
        case 2:
          return (
            <SpecificAlertTypeSelector
              selectedSpecificAlertType={selectedSpecificAlertType}
              onSpecificAlertTypeToggle={handleSpecificAlertTypeToggle}
            />
          );
        case 3:
          return (
            <RegionSelector
              regions={regions}
              selectedRegions={selectedRegions}
              onRegionToggle={handleRegionToggle}
              onSelectAll={handleSelectAll}
              onClearAll={handleClearAll}
              isLoading={isLoadingRegions}
            />
          );
        default:
          return null;
      }
    } else {
      // Custom flow
      switch (currentStep) {
        case 1:
          return (
            <AlertTypeSelector
              selectedAlertType={selectedCustomAlertType}
              onAlertTypeToggle={handleCustomAlertTypeToggle}
            />
          );
        case 2:
          return (
            <CustomAlertCommodityConfig
              selectedAlertType={selectedCustomAlertType}
              selectedCommodity={selectedCommodity}
              onCommodityToggle={handleCommodityToggle}
              skuSelection={skuSelection}
              onSkuChange={handleSkuChange}
            />
          );
        case 3:
          return (
            <RegionSelector
              regions={regions}
              selectedRegions={selectedRegions}
              onRegionToggle={handleRegionToggle}
              onSelectAll={handleSelectAll}
              onClearAll={handleClearAll}
              isLoading={isLoadingRegions}
            />
          );
        case 4:
          return (
            <CustomAlertTypeConfiguration
              selectedAlertType={selectedCustomAlertType}
              configuration={customAlertConfiguration}
              onConfigurationChange={handleCustomConfigurationChange}
            />
          );
        default:
          return null;
      }
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-start px-4 py-10 bg-[#F0F5F2] bg-dark-app">
      <div className="flex flex-col gap-10 items-center w-full max-w-full lg:max-w-[1038px]">
        {/* Title */}
        <PageTitle title={pageTitle ?? t('alerts.configureAlerts')} />

        {/* Smart/Custom Toggle */}
        <AlertTypeToggle alertType={alertType} onAlertTypeChange={handleAlertTypeChange} />

        {/* Main Content Container */}
        <div className="bg-white dark:bg-black border border-[#E8EBE9] dark:border-transparent rounded-[8px] w-full max-w-full lg:max-w-[664px] min-h-[453px] flex flex-col px-4 sm:px-8 pb-7">
          {/* Step Header */}
          <div className="flex items-center justify-center gap-4 py-6">
            <h2
              className="font-['Inter'] font-medium text-center text-[#282A29] dark:text-white"
              style={{ fontSize: fontSize + 6 }}
            >
              {getStepTitle()}
            </h2>
          </div>

          {/* Step Content */}
          {renderStepContent()}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="w-full max-w-full lg:max-w-[664px] px-4 py-3 bg-bloom-green/20 border border-bloom-green rounded-[8px]">
            <p 
              className="font-['Inter'] text-bloom-green text-center"
              style={{ fontSize: fontSize }}
            >
              {successMessage}
            </p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="w-full max-w-full lg:max-w-[664px] px-4 py-3 bg-[#ec3460]/20 border border-[#ec3460] rounded-[8px]">
            <p 
              className="font-['Inter'] text-[#ec3460] text-center"
              style={{ fontSize: fontSize }}
            >
              {errorMessage}
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        <NavigationButtons
          onPrevious={handlePrevious}
          onNext={handleNext}
          previousDisabled={currentStep === 1 && !onExit}
          nextDisabled={!canProceed()}
          nextButtonText={currentStep === getMaxSteps() ? t('common.submit') : t('common.next')}
          nextButtonLoading={
            createSmartAlertMutation.isPending ||
            createCustomAlertMutation.isPending
          }
        />
      </div>
    </div>
  );
};

export default Alerts;
